import { Tooltip } from "components/Tooltip/Tooltip";
import type { CSSProperties } from "react";
import type { ApiError } from "types/ApiTypes";

import type {
  CostProjectionDataType,
  MSADataType,
  DocumentManagementDataType,
} from "../../../../types/DataTypes";

/**
 * possible status colors for medical records, rx history, and pay history
 */
export type StatusColor = "green" | "yellow" | "purple" | "red";

/**
 * maps a status string to its corresponding color
 */
export const getStatusColor = (status: string): StatusColor => {
  switch (status) {
    case "PRESENT_AND_PAID":
      return "green";
    case "PRESENT_NOT_PAID":
      return "purple";
    case "REQUESTED":
      return "yellow";
    case "MISSING":
    default:
      return "red";
  }
};

/**
 * maps a status string to its corresponding color specifically for medical records
 */
export const getMedicalRecordStatusColor = (
  status: string,
): "green" | "purple" | "white" => {
  switch (status) {
    case "PAID":
    case "PRESENT_AND_PAID":
      return "green";
    case "UNPAID":
    case "PRESENT_NOT_PAID":
      return "purple";
    case "UNKNOWN":
    case "NEEDS_REVIEW":
    default:
      return "white";
  }
};

export const getErrorMessage = (
  error: ApiError,
  defaultMessage: string,
): string => {
  return (
    error?.data?.detail ||
    error?.data?.message ||
    error?.message ||
    defaultMessage
  );
};

/**
 * common props type for all medical history tables
 */
export type MedicalHistoryTableProps = {
  service: MSADataType | CostProjectionDataType | DocumentManagementDataType;
};

/**
 * common column props for status indicators
 */
export const statusColumnProps = {
  key: "status",
  name: "Status",
  width: 60,
  minWidth: 60,
  maxWidth: 60,
};

/**
 * creates a status indicator cell renderer
 */
export const createStatusIndicatorCell = () => (
  <div
    aria-label="Status indicator"
    style={{ width: "100%", height: "100%" }}
  />
);

const ellipsisCellStyle: CSSProperties = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  width: "100%",
  maxWidth: "100%",
};

export const renderEllipsisCell = (text: string) => {
  if (!text) {
    return null;
  }

  return (
    <Tooltip content={text} place="top" anchorStyle="block-full">
      <div style={ellipsisCellStyle}>{text}</div>
    </Tooltip>
  );
};

export function createEllipsisCellRenderer<T extends Record<string, unknown>>(
  key: keyof T & string,
) {
  return ({ row }: { row: T }) => renderEllipsisCell(String(row[key] ?? ""));
}
